/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import {
  recommendationsMapPageAnalytics,
  venueDetailsPageAnalytics,
} from '../../analyticsConsts';
import { CATEGORY_ICONS, SELECTED_RECOMMENDATIONS_TYPE_ID } from '../../const';
import { useOnboardingDataContext } from '../../context/OnboardingData';
import { usePreferencesContext } from '../../context/preferences';
import useClubAccreditation from '../../hooks/useClubAccreditation';
import useMediaQuery from '../../hooks/useMediaQuery';
import normaliseSessionLogo from '../../normaliser/sessionlogo';
import { getSessionLogo } from '../../services';
import fullBookingDataLayer from '../../utils/fullBookingDataLayer';
import idFromString from '../../utils/idFromString';
import publicPath from '../../utils/publicPath';
import AccredationLevel from '../AccredationLevel';
import Button from '../Button';
import ExternalLink from '../ExternalLink';
import { MoreInfoRightArrow, SportsSoccer } from '../Icons';
import Image from '../Image';
import useStyles from './styles';

const { VENUE_SESSION_CARD_CTA_BOOKING } = venueDetailsPageAnalytics;
const { MORE_INFO } = recommendationsMapPageAnalytics;

const CardListView = forwardRef(
  (
    {
      cardType = 'club', // cardType (club , sessional)
      index,
      ClubName,
      OrganizationName,
      DistanceFromUserLocation,
      AddressLine1,
      PostCode,
      CentreName,
      TeamCount,
      Age,
      Gender,
      Disability,
      PlaysOnDay,
      SessionTime,
      Price = 0,
      Category,
      CategoryTypeID,
      BookingURL,
      FootballType,
      ClubId,
      WgsClubId: accreditationid,
      DiscoverFootballType, // like 11v11, Futsal, U23, Football, U21 etc..
      onClick,
      isSelected = false,
      isLink = true,
    },
    ref
  ) => {
    const priceDetails =
      typeof Price === 'number' && !isNaN(Price)
        ? Price === 0
          ? 'Free'
          : `£${Price.toFixed(2)}`
        : 0;
    const styles = useStyles();
    const history = useHistory();
    const categoryStringId = idFromString(Category);
    const [deskLogo, setDeskLogo] = useState();
    const [mobLogo, setMobLogo] = useState();
    const [logoDetails, setLogoDetails] = useState();
    const categoryHasIcon = CATEGORY_ICONS.includes(categoryStringId);
    const isClub = cardType === 'club';
    const isDesktop = useMediaQuery('screen and (min-width: 768px)');
    const isMobile = useMediaQuery(`screen and (max-width: 1023px)`);
    const { preferences, setPreferences } = usePreferencesContext();
    const { Age: age } = preferences;
    const { OnboardingContext } = useOnboardingDataContext();
    const cardUrl = `/club/${ClubId}`;
    let desktopLogoRef = useRef();
    let mobLogoRef = useRef();

    // when we get the club accrediationId we want to fetch the accreditation details
    const accreditation = useClubAccreditation(accreditationid);

    const setRecommendationsPreference = e => {
      // we want to block the default link and only navigate
      // when we are sure the preferences have been set
      e.preventDefault();

      setPreferences({
        ...preferences,
        [SELECTED_RECOMMENDATIONS_TYPE_ID]: DiscoverFootballType,
      });

      history.push(cardUrl);
    };

    useEffect(() => {
      getSessionLogo()
        .then(({ data }) => {
          const logoData = normaliseSessionLogo(data);
          setLogoDetails(logoData);
        })
        .catch(() => {});
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (logoDetails) {
        logoDetails?.session?.map(event => {
          const categoryId = event?.sessionId;
          if (parseInt(categoryId) === CategoryTypeID) {
            desktopLogoRef.current = event?.images?.desktop?.src;
            mobLogoRef.current = event?.images?.mobile?.src;
            setDeskLogo(desktopLogoRef.current);
            setMobLogo(mobLogoRef.current);
          }
          return categoryId;
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logoDetails, CategoryTypeID]);

    const sitecoreLogo = isDesktop ? deskLogo : mobLogo;
    const SessionName = OrganizationName;
    const venueTitle = CentreName;

    let tagsCount = 0;
    let totalTags = 0;
    if (!isClub) {
      Age && (totalTags = totalTags + 1);
      Gender && (totalTags = totalTags + 1);
      Disability && (totalTags = totalTags + 1);
      PlaysOnDay && (totalTags = totalTags + 1);
      SessionTime && (totalTags = totalTags + 1);
      priceDetails && (totalTags = totalTags + 1);
    }

    return (
      <ExternalLink
        // The ref is correctly passed to the focusable element here
        ref={ref}
        id={`${VENUE_SESSION_CARD_CTA_BOOKING}-${index}`}
        href={isClub ? cardUrl : BookingURL}
        isLink={isLink}
        onClick={
          isClub
            ? setRecommendationsPreference
            : () => {
                fullBookingDataLayer({
                  event_name: 'cta_primary_click',
                  eventCategory: venueTitle,
                  eventAction: 'click - Full details & Booking',
                  eventLabel: SessionName,
                  eventparameter1: age,
                  eventparameter2: Category,
                  eventparameter3: OnboardingContext.gender_group,
                  isVenueDetails: true,
                  Age_group: age,
                  Category_type: Category?.toLowerCase(),
                  Gender_group: OnboardingContext.gender_group,
                  Venue_session: SessionName?.toLowerCase(),
                  Venue_name: venueTitle?.toLowerCase(),
                  link_text: 'full details & booking',
                  price: priceDetails,
                  football_type: FootballType,
                });
              }
        }
        aria-label={`Full details & booking for ${SessionName} - opens in a new window`}
        css={styles.cardLink}
      >
        <div
          css={styles.cardStyle(isLink, isSelected)}
          onClick={() => onClick?.(ClubId)}
          role="button"
        >
          <div css={styles.logoStyle}>
            <div css={styles.accrediation}>
              {isClub ? (
                <>
                  <Image
                    css={styles.logoImgStyle}
                    alt="Club logo"
                    src={publicPath(
                      `/images/mobile/club/hero/club-ef-logo.svg`
                    )}
                  />
                </>
              ) : (
                <>
                  <Image
                    css={styles.categoryLogo}
                    src={
                      sitecoreLogo
                        ? sitecoreLogo
                        : `/images/desktop/initiativeLogo/${
                            categoryHasIcon ? categoryStringId : 'ef-logo'
                          }.svg`
                    }
                    alt="category logo"
                  />
                </>
              )}

              {isClub && accreditation && accreditation.isAccreditation && (
                <>
                  <div css={styles.clubAccrediation}>
                    <img
                      src={publicPath(
                        `/images/common/ef-accredited-club-rating.svg`
                      )}
                      alt={`This club has an FA Accreditation`}
                      width="100%"
                    />
                    {accreditation && (
                      <AccredationLevel {...accreditation} cardList={true} />
                    )}
                  </div>
                </>
              )}
            </div>
            <div css={styles.sessionTitleStyle(isClub)}>
              <span css={styles.footballIcon}>
                <SportsSoccer color={isClub ? 'white' : '#011E41'} />
              </span>
              <span>{isClub ? 'Team football' : 'Sessional football'}</span>
            </div>
          </div>
          <div css={styles.titleLocation}>
            <div css={styles.headingStyle}>
              {isClub ? ClubName : OrganizationName}
            </div>
            <div css={styles.locationStyle}>
              <span css={styles.distanceIcon}>
                <svg
                  width="8"
                  height="10"
                  viewBox="0 0 8 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.00018 5.00006C4.27518 5.00006 4.5106 4.90215 4.70643 4.70631C4.90227 4.51048 5.00018 4.27506 5.00018 4.00006C5.00018 3.72506 4.90227 3.48964 4.70643 3.29381C4.5106 3.09798 4.27518 3.00006 4.00018 3.00006C3.72518 3.00006 3.48977 3.09798 3.29393 3.29381C3.0981 3.48964 3.00018 3.72506 3.00018 4.00006C3.00018 4.27506 3.0981 4.51048 3.29393 4.70631C3.48977 4.90215 3.72518 5.00006 4.00018 5.00006ZM4.00018 8.67506C5.01685 7.74173 5.77102 6.89381 6.26268 6.13131C6.75435 5.36881 7.00018 4.69173 7.00018 4.10006C7.00018 3.19173 6.7106 2.44798 6.13143 1.86881C5.55227 1.28964 4.84185 1.00006 4.00018 1.00006C3.15852 1.00006 2.4481 1.28964 1.86893 1.86881C1.28977 2.44798 1.00018 3.19173 1.00018 4.10006C1.00018 4.69173 1.24602 5.36881 1.73768 6.13131C2.22935 6.89381 2.98352 7.74173 4.00018 8.67506ZM4.00018 10.0001C2.65852 8.8584 1.65643 7.79798 0.993933 6.81881C0.331433 5.83964 0.000183105 4.93339 0.000183105 4.10006C0.000183105 2.85006 0.402266 1.85423 1.20643 1.11256C2.0106 0.370894 2.94185 6.10352e-05 4.00018 6.10352e-05C5.05852 6.10352e-05 5.98977 0.370894 6.79393 1.11256C7.5981 1.85423 8.00018 2.85006 8.00018 4.10006C8.00018 4.93339 7.66893 5.83964 7.00643 6.81881C6.34393 7.79798 5.34185 8.8584 4.00018 10.0001Z"
                    fill="#1D1D1B"
                  />
                </svg>
              </span>
              <span css={styles.text}>
                {DistanceFromUserLocation} MILE AWAY |{' '}
                {isClub ? `${AddressLine1}, ${PostCode}` : CentreName}
              </span>
            </div>
          </div>
          <div css={styles.infoTags}>
            {isClub ? (
              <div css={styles.infoCapsule(isClub, totalTags)}>
                {TeamCount} teams available
              </div>
            ) : (
              <>
                {Age && (
                  <div
                    css={styles.infoCapsule(isClub, totalTags)}
                    className={`tag-${tagsCount++}`}
                  >
                    Age {Age}
                  </div>
                )}
                {Gender && (
                  <div
                    css={styles.infoCapsule(isClub, totalTags)}
                    className={`tag-${tagsCount++}`}
                  >
                    {Gender}
                  </div>
                )}
                {Disability && (
                  <div
                    css={styles.infoCapsule(isClub, totalTags)}
                    className={`tag-${tagsCount++}`}
                  >
                    {Disability}
                  </div>
                )}
                {PlaysOnDay && (
                  <div
                    css={styles.infoCapsule(isClub, totalTags)}
                    className={`tag-${tagsCount++}`}
                  >
                    {PlaysOnDay}
                  </div>
                )}
                {SessionTime && (
                  <div
                    css={styles.infoCapsule(isClub, totalTags)}
                    className={`tag-${tagsCount++}`}
                  >
                    {SessionTime}
                  </div>
                )}
                {priceDetails && (
                  <div
                    css={styles.infoCapsule(isClub, totalTags)}
                    className={`tag-${tagsCount++}`}
                  >
                    {priceDetails}
                  </div>
                )}
              </>
            )}
          </div>
          {isSelected && !isMobile && (
            <div css={styles.cardMoreInfoWrapper}>
              <Button.Link
                primary
                id={`${MORE_INFO}-${index}`}
                css={styles.primaryBtn}
                to={isClub ? cardUrl : BookingURL}
                aria-label={`More info on ${
                  isClub ? ClubName : OrganizationName
                }`}
                isExternal
                onClick={
                  isClub
                    ? setRecommendationsPreference
                    : () => {
                        fullBookingDataLayer({
                          event_name: 'cta_primary_click',
                          eventCategory: venueTitle,
                          eventAction: 'click - Full details & Booking',
                          eventLabel: SessionName,
                          eventparameter1: age,
                          eventparameter2: Category,
                          eventparameter3: OnboardingContext.gender_group,
                          isVenueDetails: true,
                          Age_group: age,
                          Category_type: Category?.toLowerCase(),
                          Gender_group: OnboardingContext.gender_group,
                          Venue_session: SessionName?.toLowerCase(),
                          Venue_name: venueTitle?.toLowerCase(),
                          link_text: 'full details & booking',
                          price: priceDetails,
                          football_type: FootballType,
                        });
                      }
                }
              >
                More info
                <MoreInfoRightArrow isClub={isClub} css={styles.arrowRotate} />
              </Button.Link>
            </div>
          )}
        </div>
      </ExternalLink>
    );
  }
); // forwardRef closes here

CardListView.propTypes = {
  cardType: PropTypes.string,
  ClubName: PropTypes.string,
  OrganizationName: PropTypes.string,
  DistanceFromUserLocation: PropTypes.number,
  AddressLine1: PropTypes.string,
  CentreName: PropTypes.string,
  TeamCount: PropTypes.number,
  Age: PropTypes.any,
  Gender: PropTypes.string,
  Disability: PropTypes.any,
  PlaysOnDay: PropTypes.any,
  SessionTime: PropTypes.any,
  Price: PropTypes.number,
  Category: PropTypes.string,
  CategoryTypeID: PropTypes.number,
  // PropTypes for all other destructured props should ideally be added here
};

export default CardListView;
